import { useMemo, useState } from 'react'
import { monthGrid, monthLabel, isPast, isoDate, fromIso } from '../lib/dates'

// Simple month calendar. `selected` is an ISO string. `minDate` blocks days
// before it, `maxDate` blocks days after it (Same-Day can only be booked
// for today or tomorrow). `accent` picks the selected-day color: 'aqua'
// (default) or 'green'.
export default function Calendar({ selected, onSelect, minDate, maxDate, accent = 'aqua' }) {
  const [cursor, setCursor] = useState(() => {
    const base = selected ? fromIso(selected) : new Date()
    return new Date(base.getFullYear(), base.getMonth(), 1)
  })

  const cells = useMemo(
    () => monthGrid(cursor.getFullYear(), cursor.getMonth()),
    [cursor],
  )

  const todayIso = isoDate(new Date())
  const selectedClass =
    accent === 'green'
      ? 'bg-green text-white font-extrabold'
      : 'bg-aqua text-white font-extrabold'

  return (
    <div className="rounded-[10px] border border-line bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() =>
            setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))
          }
          className="flex h-6 w-6 items-center justify-center rounded-md text-navy hover:bg-line/60"
          aria-label="Previous month"
        >
          ‹
        </button>
        <div className="font-bricolage text-[13px] font-bold text-navy">
          {monthLabel(cursor)}
        </div>
        <button
          type="button"
          onClick={() =>
            setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))
          }
          className="flex h-6 w-6 items-center justify-center rounded-md text-navy hover:bg-line/60"
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="mb-1 grid grid-cols-7 gap-0.5 text-center text-[9px] font-bold uppercase tracking-wide text-ink-3">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i}>{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="aspect-square" />
          const past = isPast(c.iso)
          const blockedByMin = minDate && c.iso < minDate
          const blockedByMax = maxDate && c.iso > maxDate
          const disabled = past || blockedByMin || blockedByMax
          const isSelected = selected === c.iso
          const isToday = c.iso === todayIso
          return (
            <button
              type="button"
              key={c.iso}
              onClick={() => !disabled && onSelect(c.iso)}
              disabled={disabled}
              className={[
                'aspect-square rounded-md text-[11px] font-semibold transition',
                disabled ? 'cursor-not-allowed text-ink-3 line-through opacity-40' : 'text-navy hover:bg-line/50',
                isToday && !isSelected ? 'bg-[#F3F4F6] font-extrabold' : '',
                isSelected ? selectedClass : '',
              ].join(' ')}
            >
              {c.day}
            </button>
          )
        })}
      </div>
    </div>
  )
}
